import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  Dispatch,
  SetStateAction,
} from "react";
import { ICountryDetail } from "../common/Interfaces/ICountryDetail";

export interface User {
  isUserSignedIn: boolean;
  isUserAuthorized: boolean;
  userRole: number;
  userRegions: string[];
  genericRole: number;
  countries: ICountryDetail[] | [];
  lineItemSelectedPivot: string;
  dateFormat: string; // Added date format preference
  selectedLanguage: string; // Added language preference for Japan
}

export const LocalStorageContextDefaultValues = {
  isUserSignedIn: false,
  isUserAuthorized: false,
  userRole: -1,
  userRegions: [],
  genericRole: -1,
  countries: [],
  lineItemSelectedPivot: '',
  dateFormat: 'DD-MM-YYYY', // Default date format
  selectedLanguage: 'ja', // Default to Japanese for Japan
};

interface LocalStorageContextProps extends User {
  setUser: Dispatch<SetStateAction<User>>;
}

const LocalStorageContext = createContext<LocalStorageContextProps | undefined>(
  undefined
);

export const useLocalStorageContext = (): LocalStorageContextProps => {
  const context = useContext(LocalStorageContext);
  if (!context) {
    throw new Error(
      "useLocalStorageContext must be used within a LocalStorageProvider"
    );
  }
  return context;
};

export const LocalStorageProvider: React.FC = ({ children }) => {
  const [user, setUser] = useState<User>({
    isUserSignedIn: localStorage.getItem("isUserSignedIn") === "true" || false,
    isUserAuthorized:
      localStorage.getItem("isUserAuthorized") === "true" || false,
    userRole: Number(localStorage.getItem("userRole")) || -1,
    userRegions: JSON.parse(localStorage.getItem("userRegions") || "[]"),
    genericRole: Number(localStorage.getItem("genericRole")) || -1,
    countries: JSON.parse(localStorage.getItem("countries") || "[]"),
    lineItemSelectedPivot: localStorage.getItem("lineItemSelectedPivot") || '',
    dateFormat: localStorage.getItem("dateFormat") || 'DD-MM-YYYY',
    selectedLanguage: localStorage.getItem("selectedLanguage") || 'ja', // Default to Japanese
  });

  useEffect(() => {
    // Update localStorage when the user object changes
    localStorage.setItem("user", JSON.stringify(user));
    localStorage.setItem("selectedLanguage", user.selectedLanguage);
  }, [user]);

  const contextValue: LocalStorageContextProps = {
    ...user,
    setUser,
  };

  return (
    <LocalStorageContext.Provider value={contextValue}>
      {children}
    </LocalStorageContext.Provider>
  );
};
